import {createTypesenseClient} from "@buildwithflux/search";
import {useEffect, useState} from "react";
import type {Client} from "typesense";

import {getAccountsApiBase} from "../utils/apiBase";
import {fetchWithRetry} from "../utils/fetchWithRetry";

import type {ClientInitialization} from "./clientInitialization";

const TYPESENSE_HOST = "8iq4spr5t63o1vlgp.a1.typesense.net";

interface TypesenseAuthResponse {
    scopedSearchKey: string;
}

async function fetchScopedKey(): Promise<string> {
    const res = await fetchWithRetry(() =>
        fetch(`${getAccountsApiBase()}/accounts/auth/typesense`, {
            method: "POST",
            headers: {"Content-Type": "application/json"},
            body: JSON.stringify({}),
        }),
    );
    if (!res.ok) {
        throw new Error(`Typesense auth failed: ${res.status}`);
    }
    const data = (await res.json()) as TypesenseAuthResponse;
    return data.scopedSearchKey;
}

/** Pass `enabled: false` on surfaces that never search these collections. */
export function useTypesenseClientInitialization(enabled = true): ClientInitialization<Client> {
    const [result, setResult] = useState<ClientInitialization<Client>>({
        client: null,
        status: enabled ? "loading" : "disabled",
    });

    useEffect(() => {
        if (!enabled) {
            setResult({client: null, status: "disabled"});
            return;
        }
        let cancelled = false;
        setResult({client: null, status: "loading"});

        fetchScopedKey()
            .then((key) => {
                if (cancelled) return;
                setResult({
                    client: createTypesenseClient({
                        host: TYPESENSE_HOST,
                        port: 443,
                        protocol: "https",
                        apiKey: key,
                        cacheSearchResultsForSeconds: 10,
                    }),
                    status: "ready",
                });
            })
            .catch((err) => {
                if (cancelled) return;
                // eslint-disable-next-line no-console
                console.error("Failed to initialize Typesense client:", err);
                setResult({client: null, status: "error"});
            });

        return () => {
            cancelled = true;
        };
    }, [enabled]);

    return result;
}

export function useTypesenseClient(enabled = true): Client | null {
    return useTypesenseClientInitialization(enabled).client;
}
